import React, { useCallback } from 'react';
import {
    Box,
    Typography,
    Alert,
    Skeleton,
} from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Project } from '../types';
import { useProjects } from '../hooks/useProjects';
import { useUsers } from '../hooks/useUsers';
import ProjectsTableAgGrid from '../components/ProjectsTableAgGrid';
import ErrorBoundary from '../components/ErrorBoundary';

const ProjectSelectionPage: React.FC = () => {
    const navigate = useNavigate();

    // Data fetching hooks
    const { projects, loading, error } = useProjects();
    const { users } = useUsers();

    const handleProjectClick = useCallback((project: Project) => {
        navigate(`/projects/${project.id}/documents`);
    }, [navigate]);

    const handleEditClick = useCallback((projectId: number) => {
        navigate(`/projects/${projectId}/edit`);
    }, [navigate]);

    if (loading) {
        return (
            <Box sx={{ height: 400 }}>
                <Skeleton variant="rectangular" height="100%" />
            </Box>
        );
    }

    return (
        <ErrorBoundary>
            <Box>
                <Box display="flex" alignItems="center" mb={4}>
                    <SettingsIcon sx={{ mr: 2, color: 'primary.main', fontSize: '2rem' }} />
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{ fontWeight: 500 }}
                    >
                        Select Project to Edit
                    </Typography>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} role="alert">
                        {error}
                    </Alert>
                )}

                <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                    Choose a project from the list below and click the settings icon to modify its details.
                </Typography>

                <Box sx={{ height: 'calc(100vh - 280px)', width: '100%' }}>
                    <ProjectsTableAgGrid
                        projects={projects}
                        users={users}
                        onProjectClick={handleProjectClick}
                        onEditClick={handleEditClick}
                    />
                </Box>
            </Box>
        </ErrorBoundary>
    );
};

export default ProjectSelectionPage;
